import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { DashboardService } from "./dashboard.service";
import { ReadinessScoreCounts, RecentCodeAssessment, TopApplication } from './dashboard.models';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Router } from "@angular/router";
import { SpinnerComponent } from "../shared/spinner/spinner.component";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ApplicationsStateService } from "../services/common/applications-state.service";
import { ChatContainerComponent } from "../chat/chat-container/chat-container.component";
import { DashboardSkeletonComponent } from "./dashboard-skeleton/dashboard-skeleton.component";
import { SkeletonComponent } from "../shared/skeleton/skeleton.component";
import { GlobalSearchComponent } from "../shared/global-search/global-search.component";

interface KpiCard {
  title: string;
  value: string | number;
  displayValue: number;
  footerText: string;
  footerLink?: string;
  accentClass: string;
  icon: string;
  badgeText?: string;
  assessmentStatus?: string;
  strategyStatus?: string;
  color?: string;
}

@Component({
  selector: "modernization-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.css"],
  imports: [CommonModule, SpinnerComponent, MatIconModule, MatTooltipModule, ChatContainerComponent, DashboardSkeletonComponent, SkeletonComponent, GlobalSearchComponent]
})
export class DashboardComponent implements OnInit {
  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private applicationsStateService: ApplicationsStateService
  ) { }

  isLoading: boolean = false;
  errorMessage: string | null = null;

  totalApplicationsKpi: KpiCard | null = null;
  strategyKpis: KpiCard[] = [];

  strategies: { key: string; label: string; count: number; percent: number; variant: "primary" | "danger" }[] = [];

  recentCodeAssessments: RecentCodeAssessment[] = [];
  isLoadingRecentCodeAssessments: boolean = false;
  completedAssessments: number = 0;
  failedAssessments: number = 0;

  isLoadingTopApps: boolean = false;
  topModernizationApps: TopApplication[] = [];
  topDecommApps: TopApplication[] = [];
  modScores: ReadinessScoreCounts = { low: 0, medium: 0, high: 0, total: 0 };
  decScores: ReadinessScoreCounts = { low: 0, medium: 0, high: 0, total: 0 };

  strategyAnimatedPercents: number[] = [];

  // Chevron Corporate Colors
  // Allowed color palette from Chevron brand guidelines PDF (excluding the 4 main strategies)
  private allowedColors = [
    '#0B2D71', // dark blue
    '#0066B2', // medium blue
    '#003653', // dark teal
    '#00708C', // medium teal
    '#444B0D', // dark green
    '#769231', // medium green
    '#3A0D36', // dark purple
    '#751269', // medium purple
  ];
  private strategyColorMap: { [key: string]: string } = {};
  private allowedColorIndex = 0;

  // Fixed color assignment for the 4 main strategies
  private strategyColorOverrides: { [key: string]: string } = {
    'modernize': '#0066B2',
    'already modern': '#00B2BD',
    'decommission': '#E5601F',
    'excluded': '#8C8F93',
  };

  private strategyIconMap: { [key: string]: string } = {
    'modernize': 'apps',
    'decommission': 'delete_outline',
    'already modern': 'verified',
    'excluded': 'block',
  };

  private strategyAccentMap: { [key: string]: string } = {
    'modernize': 'c-blue',
    'already modern': 'c-teal',
    'decommission': 'c-red',
    'excluded': 'c-grey',
  };

  getStrategyColor(strategyKey: string): string {
    // Normalize key for case-insensitive matching
    const normalizedKey = strategyKey.toLowerCase();

    // Return cached color if already assigned
    if (this.strategyColorMap[normalizedKey]) {
      return this.strategyColorMap[normalizedKey];
    }

    // Check explicit overrides first (case-insensitive)
    const overrideColor = this.strategyColorOverrides[normalizedKey];
    if (overrideColor) {
      this.strategyColorMap[normalizedKey] = overrideColor;
      return overrideColor;
    }

    // Assign next available allowed color (skipping those already used by overrides)
    let color;
    while (this.allowedColorIndex < this.allowedColors.length) {
      color = this.allowedColors[this.allowedColorIndex];
      this.allowedColorIndex++;
      // Skip if color is already used by an override
      if (!Object.values(this.strategyColorOverrides).includes(color)) {
        break;
      }
    }
    // Fallback: if all allowed colors are used, reuse from the start (excluding override colors)
    if (!color) {
      const nonOverrideColors = this.allowedColors.filter(c => !Object.values(this.strategyColorOverrides).includes(c));
      color = nonOverrideColors[(this.allowedColorIndex - 1) % nonOverrideColors.length];
    }
    this.strategyColorMap[normalizedKey] = color;
    return color;
  }

  getStrategyLabel(strategyKey: string): string {
    // If the key already has spaces (e.g. "Already Modern"), use it as-is with title case
    if (strategyKey.includes(' ')) {
      return strategyKey;
    }
    // Convert camelCase to Title Case with spaces
    return strategyKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  trackByKpiTitle(_index: number, item: KpiCard): string {
    return item.title;
  }

  trackByStrategyLabel(_index: number, item: { label: string }): string {
    return item.label;
  }

  trackByRecentAppName(_index: number, item: any): string {
    return item.name || item.appName;
  }

  onCandidateClick(app: { trackedBusinessApplicationId: number }): void {
    this.router.navigate(['/assessment', app.trackedBusinessApplicationId], { queryParams: { source: 'dashboard' } });
  }

  onPriorityRowClick(type: string, priority: string): void {
    this.router.navigate(['/applications'], { queryParams: { type, priority } });
  }

  onViewCodeAssessmentDetails(id: number): void {
    this.router.navigate(['/assessment', id], { queryParams: { source: 'dashboard' } });
  }

  onAssessmentClick(event: Event, status: string): void {
    event.preventDefault();
    this.router.navigate(['/applications'], { queryParams: { assessmentStatus: status } });
  }

  getStrategyOutcomeTooltip(label: string): string {
    const normalized = (label || '').trim().toLowerCase();
    if (normalized === 'modernize') {
      return 'Modernize: Needs modernization based on CMDB signals such as legacy architecture, hosting profile, or technical debt.';
    }
    if (normalized === 'already modern') {
      return 'Already Modern: The application appears modern in CMDB and does not currently require major modernization.';
    }
    if (normalized === 'excluded') {
      return 'Excluded: The application is out of scope for modernization, for example SaaS/COTS, not in use, sensitive data, or end-of-life.';
    }
    return '';
  }

  getAnimatedPercent(index: number): number {
    return this.strategyAnimatedPercents[index] ?? 0;
  }

  get filteredStrategiesForChart(): Array<{ key: string; label: string; count: number; percent: number; scaledPercent: number; variant: 'primary' | 'danger' }> {
    const filtered = this.strategies.filter(s => s.key.toLowerCase() !== 'decommission');
    const filteredTotal = filtered.reduce((sum, s) => sum + s.count, 0);
    return filtered.map(s => ({
      ...s,
      scaledPercent: filteredTotal > 0 ? parseFloat((s.count / filteredTotal * 100).toFixed(2)) : 0
    }));
  }

  ngOnInit(): void {
    this.applicationsStateService.clearKeys(['assessmentstatus', 'strategyStatus']);
    this.isLoading = true;
    forkJoin({
      dashboard: this.dashboardService.getDashboard(),
      strategies: this.dashboardService.getStrategies()
    }).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: ({ dashboard, strategies }) => {
        {
          if (dashboard.totalRecords == 0) {
            this.errorMessage = "There are no applications assigned to this role or owned by this user.";
          }
          else {
            // Build Total Applications KPI
            this.totalApplicationsKpi = {
              title: "Total Applications",
              value: dashboard.totalRecords,
              displayValue: dashboard.totalRecords,
              footerText: "View all applications",
              footerLink: "/applications",
              accentClass: "c-blue",
              icon: "apps",
              color: "#0066B2",
            };

            const strategyCounts = strategies.strategyCounts || {};

            const total = Object.values(strategyCounts).reduce((sum, count) => sum + count, 0);

            // Build strategies array dynamically from strategyCounts
            this.strategies = Object.entries(strategyCounts).map(([key, count]) => ({
              key: key,
              label: this.getStrategyLabel(key),
              count: count,
              percent: total ? parseFloat((count / total * 100).toFixed(2)) : 0,
              variant: key === 'Retire' ? 'danger' as const : 'primary' as const
            }));

            // Initialize strategy animated percents directly to final values
            this.strategyAnimatedPercents = this.strategies.map(s => s.percent);

            this.completedAssessments = strategies.completedAssessments || 0;
            this.failedAssessments = strategies.failedAssessments || 0;

            // Build KPI cards from strategies for the 4-card strip
            this.strategyKpis = this.strategies.map((s) => ({
              title: s.label,
              value: s.count,
              displayValue: s.count,
              footerText: s.percent + '%',
              footerLink: '/applications',
              accentClass: this.strategyAccentMap[s.key.toLowerCase()] || 'c-blue',
              icon: this.strategyIconMap[s.key.toLowerCase()] || 'category',
              assessmentStatus: s.key,
              color: this.getStrategyColor(s.key),
            }));
          }
        }
      },
      error: (error) => {
        this.errorMessage = error.error?.title;
      }
    });

    this.loadTopApplications();
    this.loadRecentCodeAssessments();
  }

  onKpiActionClick(event: Event, kpi: KpiCard, assessmentStatus: string): void {
    event.preventDefault();
    if (assessmentStatus) {
      this.router.navigate([kpi.footerLink], { queryParams: { assessmentStatus } });
    } else {
      this.router.navigate([kpi.footerLink]);
    }
  }

  onKpiStrategyActionClick(event: Event, kpi: KpiCard, strategyStatus: string): void {
    event.preventDefault();
    if (strategyStatus) {
      this.router.navigate([kpi.footerLink], { queryParams: { strategyStatus: strategyStatus } });
    } else {
      this.router.navigate([kpi.footerLink]);
    }
  }

  private loadRecentCodeAssessments(): void {
    this.isLoadingRecentCodeAssessments = true;
    this.dashboardService.getRecentCodeAssessments().pipe(
      finalize(() => this.isLoadingRecentCodeAssessments = false)
    ).subscribe({
      next: (data) => {
        this.recentCodeAssessments = data || [];
      },
      error: () => undefined
    });
  }

  private loadTopApplications(): void {
    this.isLoadingTopApps = true;
    this.dashboardService.getTopApplications().pipe(
      finalize(() => this.isLoadingTopApps = false)
    ).subscribe({
      next: (topApplications) => {
        this.topModernizationApps = topApplications.topModernizationApplications || [];
        this.topDecommApps = topApplications.topDecommissioningApplications || [];
        this.modScores = topApplications.modernizationReadinessScoreCounts || { low: 0, medium: 0, high: 0, total: 0 };
        this.decScores = topApplications.decommissioningReadinessScoreCounts || { low: 0, medium: 0, high: 0, total: 0 };
      },
      error: () => undefined
    });

    this.dashboardService.getCodeValidationConflictCandidates(5).subscribe({
      next: (data) => {
        this.topModernizationApps = (data.candidates || []).map(candidate => ({
            trackedBusinessApplicationId: candidate.trackedBusinessApplicationId,
            name: candidate.name || candidate.appId || 'Unnamed application',
            readinessScore: candidate.readinessScore,
            lightweightCodeAssessmentScore: candidate.lightweightCodeAssessmentScore,
            rationale: candidate.codeAssessmentRationale,
            aiKeyReason: candidate.codeAssessmentRationale,
            businessOwner: candidate.businessOwner
        }));
      }
    });
  }

  getDonutDash(value: number, total: number): string {
    const circumference = 2 * Math.PI * 45;
    const arcLength = total > 0 ? (value / total) * circumference : 0;
    return `${arcLength} ${circumference}`;
  }

  getDonutOffset(value: number, prevSum: number, total: number): string {
    const circumference = 2 * Math.PI * 45;
    if (total <= 0) return `${circumference}`;
    const arcLength = (value / total) * circumference;
    const startFraction = prevSum / total;
    return `${arcLength + circumference * (1 - startFraction)}`;
  }
}
